/* eslint-disable class-methods-use-this */
import AuthAPI from '../api/auth';
import BaseActionCableConnector from '../../shared/helpers/BaseActionCableConnector';
import DashboardAudioNotificationHelper from './AudioAlerts/DashboardAudioNotificationHelper';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { emitter } from 'shared/helpers/mitt';

class ActionCableConnector extends BaseActionCableConnector {
  constructor(app, pubsubToken) {
    const { websocketURL = '' } = window.chatwootConfig || {};
    super(app, pubsubToken, websocketURL);
    this.CancelTyping = [];

    this.events = {
      'custom_filter.created': this.onCustomFilterCreated,
      'custom_filter.updated': this.onCustomFilterUpdated,
      'custom_filter.deleted': this.onCustomFilterDeleted,
      'automation.created': this.onAutomationCreated,
      'automation.updated': this.onAutomationUpdated,
      'automation.deleted': this.onAutomationDeleted,
      'message.created': this.onMessageCreated,
      'message.updated': this.onMessageUpdated,
      'conversation.created': this.onConversationCreated,
      'conversation.status_changed': this.onStatusChange,
      'user:logout': this.onLogout,
      'page:reload': this.onReload,
      'assignee.changed': this.onAssigneeChanged,
      'bulk.assignee.changed': this.onBulkAssigneeChanged,
      'conversation.typing_on': this.onTypingOn,
      'conversation.typing_off': this.onTypingOff,
      'conversation.contact_changed': this.onConversationContactChange,
      'presence.update': this.onPresenceUpdate,
      'contact.deleted': this.onContactDelete,
      'contact.updated': this.onContactUpdate,
      'conversation.mentioned': this.onConversationMentioned,
      'native_attribute_requirement.created':
        this.onNativeAttributeRequirementCreated,
      'native_attribute_requirement.deleted':
        this.onNativeAttributeRequirementDeleted,
      'notification.created': this.onNotificationCreated,
      'notification.deleted': this.onNotificationDeleted,
      'notification.updated': this.onNotificationUpdated,
      'first.reply.created': this.onFirstReplyCreated,
      'conversation.read': this.onConversationRead,
      'conversation.updated': this.onConversationUpdated,
      'account.cache_invalidated': this.onCacheInvalidate,
    };
  }

  // eslint-disable-next-line class-methods-use-this
  onReconnect = () => {
    emitter.emit(BUS_EVENTS.WEBSOCKET_RECONNECT);
  };

  // eslint-disable-next-line class-methods-use-this
  onDisconnected = () => {
    emitter.emit(BUS_EVENTS.WEBSOCKET_DISCONNECT);
  };

  isAValidEvent = data => {
    const normalize = id => (id == null ? null : parseInt(id, 10));
    const currentAccountId = normalize(
      this.app.$store.getters.getCurrentAccountId
    );
    const eventAccountId = normalize(
      data?.account_id ||
      data?.notification?.account_id ||
      data?.conversation?.account_id ||
      data?.message?.account_id ||
      data?.custom_filter?.account_id ||
      data?.customFilter?.account_id
    );
    return currentAccountId !== null && currentAccountId === eventAccountId;
  };

  onMessageUpdated = data => {
    this.app.$store.dispatch('updateMessage', data);
  };

  onPresenceUpdate = data => {
    this.app.$store.dispatch('contacts/updatePresence', data.contacts);
    this.app.$store.dispatch('agents/updatePresence', data.users);
    this.app.$store.dispatch('setCurrentUserAvailability', data.users);
  };

  onConversationContactChange = payload => {
    const { meta = {}, id: conversationId } = payload;
    const { sender } = meta || {};
    if (conversationId) {
      this.app.$store.dispatch('updateConversationContact', {
        conversationId,
        ...sender,
      });
    }
  };

  onAssigneeChanged = payload => {
    const { id } = payload;
    DashboardAudioNotificationHelper.onAssigneeChanged(payload);
    if (id) {
      this.app.$store.dispatch('updateConversation', payload);
    }
  };

  onBulkAssigneeChanged = payload => {
    (payload.conversations || []).forEach(conversation => {
      DashboardAudioNotificationHelper.onAssigneeChanged(conversation);
      this.app.$store.dispatch('updateConversation', conversation);
    });
  };

  onConversationCreated = data => {
    this.app.$store.dispatch('addConversation', data);
  };

  onConversationRead = data => {
    this.app.$store.dispatch('updateConversation', data);
  };

  // eslint-disable-next-line class-methods-use-this
  onLogout = () => AuthAPI.logout();

  onMessageCreated = data => {
    const {
      conversation: { last_activity_at: lastActivityAt },
      conversation_id: conversationId,
    } = data;
    DashboardAudioNotificationHelper.onNewMessage(data);
    this.app.$store.dispatch('addMessage', data);
    this.app.$store.dispatch('updateConversationLastActivity', {
      lastActivityAt,
      conversationId,
    });
  };

  // eslint-disable-next-line class-methods-use-this
  onReload = () => window.location.reload();

  onAutomationCreated = payload => {
    emitter.emit('automation_created', payload);
  };

  onCustomFilterCreated = payload => {
    if (!this.isAValidEvent(payload)) return;
    const customFilter =
      payload.custom_filter || payload.customFilter || payload;
    // Namespaced commit to customViews module
    this.app.$store.commit('customViews/SET_SINGLE_CUSTOM_VIEW', customFilter);
  };

  onCustomFilterUpdated = payload => {
    if (!this.isAValidEvent(payload)) return;
    const customFilter =
      payload.custom_filter || payload.customFilter || payload;
    this.app.$store.commit('customViews/SET_SINGLE_CUSTOM_VIEW', customFilter);
  };

  onCustomFilterDeleted = payload => {
    if (!this.isAValidEvent(payload)) return;
    const id =
      payload.custom_filter?.id || payload.customFilter?.id || payload.id;
    if (id) this.app.$store.commit('customViews/DELETE_CUSTOM_VIEW', id);
  };

  onAutomationUpdated = payload => {
    emitter.emit('automation_updated', payload);
  };

  onAutomationDeleted = payload => {
    emitter.emit('automation_deleted', payload);
  };

  onNativeAttributeRequirementCreated = payload => {
    emitter.emit('native_attribute_requirement.created', payload);
  };

  onNativeAttributeRequirementDeleted = payload => {
    emitter.emit('native_attribute_requirement.deleted', payload);
  };

  onStatusChange = data => {
    this.app.$store.dispatch('updateConversation', data);
  };

  onConversationUpdated = data => {
    this.app.$store.dispatch('updateConversation', data);
  };

  onTypingOn = ({ conversation, user }) => {
    const conversationId = conversation.id;
    this.clearTimer(conversationId);
    this.app.$store.dispatch('conversationTypingStatus/create', {
      conversationId,
      user,
    });
    this.initTimer({ conversation, user });
  };

  onTypingOff = ({ conversation, user }) => {
    const conversationId = conversation.id;
    this.clearTimer(conversationId);
    this.app.$store.dispatch('conversationTypingStatus/destroy', {
      conversationId,
      user,
    });
  };

  onConversationMentioned = data => {
    this.app.$store.dispatch('addMentions', data);
  };

  clearTimer = conversationId => {
    const timerEvent = this.CancelTyping[conversationId];
    if (timerEvent) {
      clearTimeout(timerEvent);
      this.CancelTyping[conversationId] = null;
    }
  };

  initTimer = ({ conversation, user }) => {
    const conversationId = conversation.id;
    // Turn off typing automatically after 30 seconds
    this.CancelTyping[conversationId] = setTimeout(() => {
      this.onTypingOff({ conversation, user });
    }, 5000);
  };

  // eslint-disable-next-line class-methods-use-this
  fetchConversationStats = () => {
    emitter.emit('fetch_conversation_stats');
    emitter.emit('fetch_overview_reports');
  };

  onContactDelete = data => {
    this.app.$store.dispatch(
      'contacts/deleteContactThroughConversations',
      data.id
    );
  };

  onContactUpdate = data => {
    this.app.$store.dispatch('contacts/updateContact', data);
  };

  onNotificationCreated = data => {
    this.app.$store.dispatch('notifications/addNotification', data);
  };

  onNotificationDeleted = data => {
    this.app.$store.dispatch('notifications/deleteNotification', data);
  };

  onNotificationUpdated = data => {
    this.app.$store.dispatch('notifications/updateNotification', data);
  };

  // eslint-disable-next-line class-methods-use-this
  onFirstReplyCreated = () => {
    emitter.emit('fetch_overview_reports');
  };

  onCacheInvalidate = data => {
    const keys = data.cache_keys;
    this.app.$store.dispatch('labels/revalidate', { newKey: keys.label });
    this.app.$store.dispatch('inboxes/revalidate', { newKey: keys.inbox });
    this.app.$store.dispatch('teams/revalidate', { newKey: keys.team });
  };
}

// Removed module-level variable
// let connectorInstance = null;

export default {
  connectorInstance: null,
  init(pubsubToken) {
    this.connectorInstance = new ActionCableConnector(window.WOOT, pubsubToken);
    return this.connectorInstance;
  },
  getConnector() {
    return this.connectorInstance;
  },
};
